import { useState, useEffect, useCallback } from "react";
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors
} from "@dnd-kit/core";
import {
  SortableContext, verticalListSortingStrategy, arrayMove, useSortable
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Layout, Eye, EyeOff, Radio, Music, MessageCircle, Heart,
  CloudRain, Calendar, Mic2, Mail, Globe, Megaphone, Image,
  Clock, Users, Play, Star, ChevronDown, Check, Headphones,
  Newspaper, ShoppingBag, Gift, Podcast, Tv, Download, Rocket, Palette,
  GripVertical, Lock
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Layout presets
const LAYOUT_PRESETS = {
  minimal: {
    label: "Minimal", desc: "Clean and simple — logo, player, social links",
    icon: Layout, preview: "#18181b",
    sections: {
      show_visualizer: false, show_chat: false, show_requests: false, show_recently_played: true,
      show_weather: false, show_dedicate: false, show_ticker: false, show_sponsor: false,
      show_top_fans: false, page_show_about: false, page_show_schedule: false,
      page_show_dj_profiles: false, page_show_events: false, page_show_contact: false,
      page_show_newsletter: false, page_show_merchandise: false, page_show_donations: false,
      page_show_podcast: false, page_show_live_video: false, page_show_on_air: true,
      page_show_install_app: false, page_show_launch_cta: false, page_show_social_links: true,
    },
  },
  classic_radio: {
    label: "Classic Radio", desc: "Traditional radio station with all the essentials",
    icon: Radio, preview: "#0f172a",
    sections: {
      show_visualizer: true, show_chat: true, show_requests: true, show_recently_played: true,
      show_weather: true, show_dedicate: true, show_ticker: true, show_sponsor: true,
      show_top_fans: false, page_show_about: true, page_show_schedule: true,
      page_show_dj_profiles: false, page_show_events: false, page_show_contact: true,
      page_show_newsletter: true, page_show_merchandise: false, page_show_donations: false,
      page_show_podcast: false, page_show_live_video: false, page_show_on_air: true,
      page_show_install_app: false, page_show_launch_cta: true, page_show_social_links: true,
    },
  },
  modern_dark: {
    label: "Modern Dark", desc: "Sleek dark design with full features",
    icon: Star, preview: "#030712",
    sections: {
      show_visualizer: true, show_chat: true, show_requests: true, show_recently_played: true,
      show_weather: true, show_dedicate: true, show_ticker: true, show_sponsor: false,
      show_top_fans: true, page_show_about: true, page_show_schedule: false,
      page_show_dj_profiles: false, page_show_events: false, page_show_contact: false,
      page_show_newsletter: true, page_show_merchandise: false, page_show_donations: false,
      page_show_podcast: false, page_show_live_video: false, page_show_on_air: true,
      page_show_install_app: false, page_show_launch_cta: true, page_show_social_links: true,
    },
  },
  premium: {
    label: "Premium", desc: "All features enabled — the complete experience",
    icon: Rocket, preview: "#1c1917",
    sections: {
      show_visualizer: true, show_chat: true, show_requests: true, show_recently_played: true,
      show_weather: true, show_dedicate: true, show_ticker: true, show_sponsor: true,
      show_top_fans: true, page_show_about: true, page_show_schedule: true,
      page_show_dj_profiles: true, page_show_events: true, page_show_contact: true,
      page_show_newsletter: true, page_show_merchandise: true, page_show_donations: true,
      page_show_podcast: false, page_show_live_video: true, page_show_on_air: true,
      page_show_install_app: true, page_show_launch_cta: false, page_show_social_links: true,
    },
  },
  talk_show: {
    label: "Talk Show / Podcast", desc: "Focus on schedule, chat, and contact",
    icon: Podcast, preview: "#1e1b4b",
    sections: {
      show_visualizer: false, show_chat: true, show_requests: false, show_recently_played: false,
      show_weather: false, show_dedicate: false, show_ticker: true, show_sponsor: true,
      show_top_fans: false, page_show_about: true, page_show_schedule: true,
      page_show_dj_profiles: true, page_show_events: true, page_show_contact: true,
      page_show_newsletter: true, page_show_merchandise: false, page_show_donations: true,
      page_show_podcast: true, page_show_live_video: true, page_show_on_air: true,
      page_show_install_app: false, page_show_launch_cta: false, page_show_social_links: true,
    },
  },
  dj_club: {
    label: "DJ / Club", desc: "Bold visuals, requests, and social engagement",
    icon: Headphones, preview: "#1a0a2e",
    sections: {
      show_visualizer: true, show_chat: true, show_requests: true, show_recently_played: true,
      show_weather: false, show_dedicate: true, show_ticker: true, show_sponsor: true,
      show_top_fans: true, page_show_about: true, page_show_schedule: true,
      page_show_dj_profiles: true, page_show_events: true, page_show_contact: false,
      page_show_newsletter: false, page_show_merchandise: true, page_show_donations: false,
      page_show_podcast: false, page_show_live_video: true, page_show_on_air: true,
      page_show_install_app: true, page_show_launch_cta: false, page_show_social_links: true,
    },
  },
  news_sports: {
    label: "News / Sports", desc: "Ticker, schedule, weather, and contact focused",
    icon: Newspaper, preview: "#0c0a09",
    sections: {
      show_visualizer: false, show_chat: false, show_requests: false, show_recently_played: true,
      show_weather: true, show_dedicate: false, show_ticker: true, show_sponsor: true,
      show_top_fans: false, page_show_about: true, page_show_schedule: true,
      page_show_dj_profiles: false, page_show_events: true, page_show_contact: true,
      page_show_newsletter: true, page_show_merchandise: false, page_show_donations: false,
      page_show_podcast: true, page_show_live_video: true, page_show_on_air: true,
      page_show_install_app: false, page_show_launch_cta: false, page_show_social_links: true,
    },
  },
};

// Color theme presets
const COLOR_THEMES = [
  { key: "midnight_purple", label: "Midnight Purple", colors: { primary_color: "#8B5CF6", secondary_color: "#22C55E", bg_color: "#050505", text_color: "#FFFFFF", card_color: "#FFFFFF" } },
  { key: "neon_blue", label: "Neon Blue", colors: { primary_color: "#3B82F6", secondary_color: "#06B6D4", bg_color: "#060918", text_color: "#FFFFFF", card_color: "#93C5FD" } },
  { key: "sunset_orange", label: "Sunset Orange", colors: { primary_color: "#F97316", secondary_color: "#EF4444", bg_color: "#120800", text_color: "#FFFFFF", card_color: "#FDBA74" } },
  { key: "emerald", label: "Emerald", colors: { primary_color: "#10B981", secondary_color: "#34D399", bg_color: "#020E07", text_color: "#FFFFFF", card_color: "#6EE7B7" } },
  { key: "rose_gold", label: "Rose Gold", colors: { primary_color: "#F472B6", secondary_color: "#FB923C", bg_color: "#120510", text_color: "#FFFFFF", card_color: "#F9A8D4" } },
  { key: "arctic", label: "Arctic", colors: { primary_color: "#06B6D4", secondary_color: "#8B5CF6", bg_color: "#051015", text_color: "#E0F2FE", card_color: "#67E8F9" } },
  { key: "crimson", label: "Crimson", colors: { primary_color: "#DC2626", secondary_color: "#F59E0B", bg_color: "#0D0000", text_color: "#FFFFFF", card_color: "#FCA5A5" } },
  { key: "gold", label: "Gold Luxe", colors: { primary_color: "#D4A017", secondary_color: "#92400E", bg_color: "#0A0800", text_color: "#FEF3C7", card_color: "#FDE68A" } },
];

const COLOR_FIELDS = [
  { key: "primary_color", label: "Primary", desc: "Buttons, highlights, accents" },
  { key: "secondary_color", label: "Secondary", desc: "Live badge, secondary accents" },
  { key: "bg_color", label: "Background", desc: "Page background color" },
  { key: "text_color", label: "Text", desc: "Main text color" },
  { key: "card_color", label: "Card Tint", desc: "Glass card tint color" },
];

// Section definitions grouped by category
const SECTION_GROUPS = [
  {
    label: "Player & Audio",
    sections: [
      { key: "show_visualizer", label: "Audio Visualizer", icon: Play, desc: "FFT bars under the player" },
      { key: "show_recently_played", label: "Recently Played", icon: Clock, desc: "Song history list" },
      { key: "page_show_on_air", label: "On Air Now", icon: Mic2, desc: "Current DJ or AutoDJ info" },
      { key: "page_show_live_video", label: "Live Video", icon: Tv, desc: "Video stream embed", inputField: {
        key: "video_url", placeholder: "Paste video embed URL (YouTube, Twitch, etc.)", type: "url"
      } },
    ],
  },
  {
    label: "Interaction",
    sections: [
      { key: "show_chat", label: "Live Chat", icon: MessageCircle, desc: "Listener chat room" },
      { key: "show_requests", label: "Song Requests", icon: Music, desc: "Listeners can request songs" },
      { key: "show_dedicate", label: "Dedications", icon: Heart, desc: "Song dedication messages" },
      { key: "show_top_fans", label: "Top Fans", icon: Users, desc: "Loyalty leaderboard" },
    ],
  },
  {
    label: "Information",
    sections: [
      { key: "page_show_about", label: "About Station", icon: Radio, desc: "Station bio / description" },
      { key: "page_show_schedule", label: "Show Schedule", icon: Calendar, desc: "Weekly show times" },
      { key: "page_show_dj_profiles", label: "DJ Profiles", icon: Headphones, desc: "Team member profiles" },
      { key: "page_show_events", label: "Events", icon: Calendar, desc: "Upcoming events" },
      { key: "show_weather", label: "Weather", icon: CloudRain, desc: "Local weather widget", sub: [
        { key: "weather_show_background", label: "Sky Photo Background", desc: "Show dynamic sky photo behind weather data" }
      ] },
      { key: "show_ticker", label: "News Ticker", icon: Megaphone, desc: "Scrolling ticker bar" },
    ],
  },
  {
    label: "Conversion & Growth",
    sections: [
      { key: "page_show_newsletter", label: "Newsletter / Follow", icon: Mail, desc: "Follow station signup" },
      { key: "page_show_contact", label: "Contact Form", icon: Mail, desc: "Booking / contact block" },
      { key: "page_show_social_links", label: "Social Links", icon: Globe, desc: "Social media links" },
      { key: "show_sponsor", label: "Sponsor Area", icon: Star, desc: "Sponsor banner block" },
      { key: "page_show_merchandise", label: "Merchandise", icon: ShoppingBag, desc: "Merch section" },
      { key: "page_show_donations", label: "Donations", icon: Gift, desc: "Donation / tip button" },
      { key: "page_show_podcast", label: "Podcast Section", icon: Podcast, desc: "On-demand episodes" },
      { key: "page_show_install_app", label: "Install App Button", icon: Download, desc: "PWA install prompt" },
      { key: "page_show_launch_cta", label: "Launch Your Station CTA", icon: Rocket, desc: "Promo for new sign-ups" },
    ],
  },
];

// Flat list of all draggable sections with zone info
const ALL_SECTIONS = [
  { id: "weather", label: "Weather", icon: CloudRain, toggleKey: "show_weather", zone: "sidebar", sub: [{ key: "weather_show_background", label: "Sky Photo Background" }] },
  { id: "onAir", label: "On Air Now", icon: Mic2, toggleKey: "page_show_on_air", zone: "sidebar" },
  { id: "newsletter", label: "Follow Station", icon: Heart, toggleKey: "page_show_newsletter", zone: "sidebar" },
  { id: "liveVideo", label: "Live Video", icon: Tv, toggleKey: "page_show_live_video", zone: "sidebar", inputField: { key: "video_url", placeholder: "e.g. rtmp://live.yoursite.com/live, YouTube URL, or HLS .m3u8", type: "url" }, rtmpFields: true },
  { id: "sponsor", label: "Sponsor Block", icon: Star, toggleKey: "show_sponsor", zone: "sidebar" },
  { id: "schedule", label: "Show Schedule", icon: Calendar, toggleKey: "page_show_schedule", zone: "sidebar" },
  { id: "chat", label: "Live Chat", icon: MessageCircle, toggleKey: "show_chat", zone: "main" },
  { id: "requests", label: "Song Requests", icon: Music, toggleKey: "show_requests", zone: "main" },
  { id: "about", label: "About Station", icon: Radio, toggleKey: "page_show_about", zone: "main" },
  { id: "history", label: "Recently Played", icon: Clock, toggleKey: "show_recently_played", zone: "main" },
  { id: "topFans", label: "Top Listeners", icon: Users, toggleKey: "show_top_fans", zone: "sidebar" },
  { id: "socialLinks", label: "Social Links", icon: Globe, toggleKey: "page_show_social_links", zone: "sidebar" },
  { id: "contact", label: "Contact Form", icon: Mail, toggleKey: "page_show_contact", zone: "sidebar" },
  { id: "merch", label: "Merchandise", icon: ShoppingBag, toggleKey: "page_show_merchandise", zone: "sidebar" },
  { id: "donations", label: "Donations", icon: Gift, toggleKey: "page_show_donations", zone: "sidebar" },
  { id: "events", label: "Events", icon: Calendar, toggleKey: "page_show_events", zone: "sidebar" },
  { id: "visualizer", label: "Audio Visualizer", icon: Play, toggleKey: "show_visualizer", zone: "player" },
  { id: "ticker", label: "News Ticker", icon: Megaphone, toggleKey: "show_ticker", zone: "player" },
  { id: "djProfiles", label: "DJ Profiles", icon: Headphones, toggleKey: "page_show_dj_profiles", zone: "sidebar" },
  { id: "podcast", label: "Podcast Section", icon: Podcast, toggleKey: "page_show_podcast", zone: "sidebar" },
  { id: "installApp", label: "Install App", icon: Download, toggleKey: "page_show_install_app", zone: "sidebar" },
  { id: "launchCta", label: "Start Your Station", icon: Rocket, toggleKey: "page_show_launch_cta", zone: "sidebar" },
  { id: "dedicate", label: "Dedications", icon: Heart, toggleKey: "show_dedicate", zone: "main" },
];

const DEFAULT_ORDER = ALL_SECTIONS.map(s => s.id);

const SECTION_MAP = {};
ALL_SECTIONS.forEach(s => { SECTION_MAP[s.id] = s; });

// Preset section orders
const ORDER_PRESETS = [
  { key: "classic_radio", label: "Classic Radio", icon: Radio, desc: "Traditional radio layout",
    order: ["onAir","weather","newsletter","schedule","chat","requests","history","about","sponsor","socialLinks","contact","topFans","liveVideo","merch","donations","events","visualizer","ticker","djProfiles","podcast","dedicate","installApp","launchCta"] },
  { key: "modern_sidebar", label: "Modern Sidebar", icon: Layout, desc: "Content-focused with rich sidebar",
    order: ["weather","onAir","liveVideo","newsletter","chat","requests","about","history","schedule","sponsor","socialLinks","topFans","contact","merch","donations","events","visualizer","ticker","djProfiles","podcast","dedicate","installApp","launchCta"] },
  { key: "minimal", label: "Minimal", icon: Star, desc: "Clean and simple — essentials only",
    order: ["onAir","history","about","newsletter","socialLinks","weather","chat","requests","schedule","sponsor","contact","topFans","liveVideo","merch","donations","events","visualizer","ticker","djProfiles","podcast","dedicate","installApp","launchCta"] },
  { key: "full_station", label: "Full Station Site", icon: Rocket, desc: "Everything enabled — the works",
    order: ["weather","onAir","newsletter","liveVideo","chat","requests","about","history","schedule","sponsor","socialLinks","topFans","contact","merch","donations","djProfiles","events","podcast","visualizer","ticker","dedicate","installApp","launchCta"] },
  { key: "video_first", label: "Video First", icon: Tv, desc: "Lead with live video stream",
    order: ["liveVideo","onAir","weather","chat","requests","newsletter","history","about","schedule","sponsor","socialLinks","topFans","contact","merch","donations","events","visualizer","ticker","djProfiles","podcast","dedicate","installApp","launchCta"] },
];

// Sortable draggable item
const SortableItem = ({ id, section, enabled, onToggle, form, setForm }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1, zIndex: isDragging ? 50 : "auto" };
  const Icon = section.icon;
  const zoneBadge = section.zone === "main" ? "Main" : section.zone === "sidebar" ? "Sidebar" : "Player";
  const zoneColor = section.zone === "main" ? "text-blue-400 bg-blue-400/10" : section.zone === "sidebar" ? "text-purple-400 bg-purple-400/10" : "text-orange-400 bg-orange-400/10";
  return (
    <div ref={setNodeRef} style={style} className={`flex flex-col rounded-xl border transition ${isDragging ? "border-purple-500/40 bg-purple-500/5 shadow-lg" : enabled ? "border-white/10 bg-white/[0.03]" : "border-white/[0.04] bg-transparent"}`}>
      <div className="flex items-center gap-2 px-3 py-2.5">
        <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-1 text-white/20 hover:text-white/50 flex-shrink-0" data-testid={`drag-${id}`}>
          <GripVertical className="w-4 h-4" />
        </div>
        <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${enabled ? "bg-purple-500/20" : "bg-white/5"}`}>
          <Icon className={`w-3.5 h-3.5 ${enabled ? "text-purple-400" : "text-white/25"}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className={`text-sm font-medium ${enabled ? "text-white" : "text-white/40"}`}>{section.label}</p>
            <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${zoneColor}`}>{zoneBadge}</span>
          </div>
        </div>
        <button onClick={() => onToggle(section.toggleKey)} className="flex-shrink-0" data-testid={`toggle-${section.toggleKey}`}>
          <div className={`w-9 h-5 rounded-full p-0.5 transition ${enabled ? "bg-purple-500" : "bg-white/10"}`}>
            <div className={`w-4 h-4 rounded-full bg-white transition-transform ${enabled ? "translate-x-4" : "translate-x-0"}`} />
          </div>
        </button>
      </div>
      {enabled && section.sub?.map(sub => {
        const subEnabled = form[sub.key] ?? true;
        return (
          <button key={sub.key} onClick={() => setForm(prev => ({ ...prev, [sub.key]: !(prev[sub.key] ?? true) }))}
            className="flex items-center gap-2 pl-12 pr-3 py-1.5 text-left hover:bg-white/[0.02]"
            data-testid={`toggle-${sub.key}`}
          >
            <p className={`text-xs ${subEnabled ? "text-white/60" : "text-white/25"}`}>{sub.label}</p>
            <div className={`ml-auto w-7 h-4 rounded-full p-0.5 ${subEnabled ? "bg-purple-500/60" : "bg-white/10"}`}>
              <div className={`w-3 h-3 rounded-full bg-white transition-transform ${subEnabled ? "translate-x-3" : "translate-x-0"}`} />
            </div>
          </button>
        );
      })}
      {enabled && section.inputField && (
        <div className="px-3 pb-2.5 pl-12 space-y-2">
          <div>
            <label className="text-white/30 text-[10px] uppercase tracking-wider font-semibold mb-1 block">Playback URL</label>
            <input type={section.inputField.type || "text"} value={form[section.inputField.key] || ""}
              onChange={e => setForm(prev => ({ ...prev, [section.inputField.key]: e.target.value }))}
              placeholder={section.inputField.placeholder}
              className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-1.5 text-white text-xs placeholder-white/20 focus:outline-none focus:border-purple-500/40"
              data-testid={`input-${section.inputField.key}`}
            />
            {section.rtmpFields && (
              <p className="text-white/20 text-[10px] mt-1">Accepts RTMP, Owncast, YouTube, Twitch, HLS, or any embed URL</p>
            )}
          </div>
          {section.rtmpFields && (
            <>
              <div className="border-t border-white/[0.06] pt-2 mt-1">
                <p className="text-white/30 text-[10px] uppercase tracking-wider font-semibold mb-1.5">OBS / RTMP Config (for DJs)</p>
              </div>
              <div>
                <label className="text-white/20 text-[10px] mb-0.5 block">RTMP Server URL</label>
                <input type="url" value={form.rtmp_server_url || ""}
                  onChange={e => setForm(prev => ({ ...prev, rtmp_server_url: e.target.value }))}
                  placeholder="rtmp://your-server.com/live"
                  className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-1.5 text-white text-xs placeholder-white/20 focus:outline-none focus:border-purple-500/40 font-mono"
                  data-testid="input-rtmp-server-url"
                />
              </div>
              <div>
                <label className="text-white/20 text-[10px] mb-0.5 block">Stream Key</label>
                <input type="text" value={form.rtmp_stream_key || ""}
                  onChange={e => setForm(prev => ({ ...prev, rtmp_stream_key: e.target.value }))}
                  placeholder="your-stream-key"
                  className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-1.5 text-white text-xs placeholder-white/20 focus:outline-none focus:border-purple-500/40 font-mono"
                  data-testid="input-rtmp-stream-key"
                />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export const PageBuilderTab = ({ currentUser, authToken }) => {
  const [whiteLabelConfig, setWhiteLabelConfig] = useState(null);
  const [whiteLabelForm, setWhiteLabelForm] = useState({});
  const [savingWhiteLabel, setSavingWhiteLabel] = useState(false);
  const [whiteLabelSuccess, setWhiteLabelSuccess] = useState('');
  const [uploadingHero, setUploadingHero] = useState(false);

  const headers = useCallback(() => {
    const h = { 'Content-Type': 'application/json' };
    if (authToken) h['Authorization'] = `Bearer ${authToken}`;
    return h;
  }, [authToken]);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/whitelabel/config`, { credentials: 'omit', headers: headers() });
        if (res.ok) {
          const data = await res.json();
          if (data.has_whitelabel && data.config) {
            setWhiteLabelConfig(data.config);
            setWhiteLabelForm(data.config);
          }
        }
      } catch {}
    };
    fetchConfig();
  }, [headers]);

  const handleSaveWhiteLabel = async () => {
    setSavingWhiteLabel(true);
    try {
      const method = whiteLabelConfig ? 'PUT' : 'POST';
      const url = whiteLabelConfig ? `${BACKEND_URL}/api/whitelabel/config` : `${BACKEND_URL}/api/whitelabel/create`;
      const res = await fetch(url, { method, credentials: 'omit', headers: headers(), body: JSON.stringify(whiteLabelForm) });
      if (res.ok) {
        const data = await res.json();
        setWhiteLabelConfig(data);
        setWhiteLabelSuccess('Page settings saved!');
        setTimeout(() => setWhiteLabelSuccess(''), 3000);
      }
    } catch {}
    finally { setSavingWhiteLabel(false); }
  };

  const handleHeroUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploadingHero(true);
    try {
      const formData = new FormData(); formData.append('file', file);
      const h = {}; if (authToken) h['Authorization'] = `Bearer ${authToken}`;
      const res = await fetch(`${BACKEND_URL}/api/whitelabel/background`, { method: 'POST', headers: h, body: formData });
      if (res.ok) { const data = await res.json(); setWhiteLabelForm(prev => ({ ...prev, page_hero_image: data.background_url })); setWhiteLabelSuccess('Hero image uploaded!'); setTimeout(() => setWhiteLabelSuccess(''), 3000); }
    } catch {}
    finally { setUploadingHero(false); }
  };

  const [expandedGroup, setExpandedGroup] = useState("Player & Audio");
  const activePreset = whiteLabelForm.page_layout_preset || "modern_dark";
  const slug = whiteLabelConfig?.slug;

  const toggleSection = (key) => {
    setWhiteLabelForm(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const applyPreset = (presetKey) => {
    const preset = LAYOUT_PRESETS[presetKey];
    if (!preset) return;
    setWhiteLabelForm(prev => ({
      ...prev,
      ...preset.sections,
      page_layout_preset: presetKey,
    }));
  };

  // Drag and drop
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }), useSensor(KeyboardSensor));
  const sectionOrder = whiteLabelForm.section_order || DEFAULT_ORDER;
  // Ensure all sections are in the order (handle new sections)
  const fullOrder = [...sectionOrder, ...DEFAULT_ORDER.filter(id => !sectionOrder.includes(id))];

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = fullOrder.indexOf(active.id);
      const newIndex = fullOrder.indexOf(over.id);
      const newOrder = arrayMove(fullOrder, oldIndex, newIndex);
      setWhiteLabelForm(prev => ({ ...prev, section_order: newOrder }));
    }
  };

  const applyOrderPreset = (preset) => {
    setWhiteLabelForm(prev => ({ ...prev, section_order: preset.order }));
  };

  const enabledCount = ALL_SECTIONS.filter(s => whiteLabelForm[s.toggleKey]).length;

  return (
    <div className="space-y-6" data-testid="page-builder-tab">

      {/* Header + Station Page URL */}
      <div className="bg-gradient-to-r from-purple-500/10 to-blue-500/10 border border-purple-500/20 rounded-2xl p-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h3 className="text-xl font-bold text-white flex items-center gap-2">
              <Layout className="w-5 h-5 text-purple-400" /> Station Page Builder
            </h3>
            <p className="text-white/50 text-sm mt-1">Customize which sections appear on your public station page</p>
          </div>
          <div className="flex items-center gap-2 text-sm text-white/40">
            <span className="font-mono" style={{ color: enabledCount > 15 ? "#f59e0b" : "#a855f7" }}>
              {enabledCount}/{ALL_SECTIONS.length}
            </span>
            sections enabled
          </div>
        </div>
        {slug && (
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <a href={`/station/${slug}`} target="_blank" rel="noopener noreferrer"
              className="text-purple-400 hover:text-purple-300 text-sm font-mono flex items-center gap-1" data-testid="station-page-link">
              <Globe className="w-3.5 h-3.5" /> /station/{slug}
            </a>
            <a href={`/player/${slug}`} target="_blank" rel="noopener noreferrer"
              className="text-white/40 hover:text-white/60 text-sm font-mono flex items-center gap-1">
              <Play className="w-3.5 h-3.5" /> /player/{slug}
            </a>
          </div>
        )}
      </div>

      {/* Default Public Page Picker */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
        <h4 className="text-white font-semibold mb-3">Default Public Page</h4>
        <p className="text-white/40 text-xs mb-4">When listeners visit your station, which page should they see?</p>
        <div className="grid grid-cols-2 gap-3">
          {[
            { value: "player", label: "Lightweight Player", desc: "Simple player for quick sharing", icon: Play },
            { value: "station", label: "Full Station Page", desc: "Complete branded station site", icon: Layout },
          ].map(opt => (
            <button key={opt.value}
              onClick={() => setWhiteLabelForm(prev => ({ ...prev, default_public_page: opt.value }))}
              className={`text-left p-4 rounded-xl border-2 transition-all ${
                whiteLabelForm.default_public_page === opt.value
                  ? "border-purple-500/60 bg-purple-500/10"
                  : "border-white/5 bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/10"
              }`}
              data-testid={`default-page-${opt.value}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <opt.icon className={`w-4 h-4 ${whiteLabelForm.default_public_page === opt.value ? "text-purple-400" : "text-white/30"}`} />
                <span className="text-white text-sm font-semibold">{opt.label}</span>
                {whiteLabelForm.default_public_page === opt.value && <Check className="w-4 h-4 text-purple-400 ml-auto" />}
              </div>
              <p className="text-white/40 text-xs">{opt.desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Hero Image Upload */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
        <h4 className="text-white font-semibold mb-1">Hero Banner Image</h4>
        <p className="text-white/40 text-xs mb-4">Large background image for the station page header</p>
        <div className="flex items-center gap-4">
          {whiteLabelForm.page_hero_image ? (
            <div className="relative group">
              <img src={whiteLabelForm.page_hero_image} alt="Hero" className="w-48 h-24 object-cover rounded-xl border border-white/10" />
              <button onClick={() => setWhiteLabelForm(prev => ({ ...prev, page_hero_image: "" }))}
                className="absolute top-1 right-1 bg-black/60 text-white/60 hover:text-red-400 rounded-lg p-1 opacity-0 group-hover:opacity-100 transition text-xs">
                Remove
              </button>
            </div>
          ) : (
            <div className="w-48 h-24 rounded-xl border-2 border-dashed border-white/10 flex items-center justify-center">
              <Image className="w-6 h-6 text-white/20" />
            </div>
          )}
          <label className="cursor-pointer px-4 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-white text-sm transition flex items-center gap-2">
            <Image className="w-4 h-4 text-white/50" />
            {uploadingHero ? "Uploading..." : "Upload Hero Image"}
            <input type="file" className="hidden" accept="image/*" onChange={handleHeroUpload} disabled={uploadingHero} data-testid="hero-upload-input" />
          </label>
        </div>
      </div>

      {/* Station Bio */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
        <h4 className="text-white font-semibold mb-1">Station Description</h4>
        <p className="text-white/40 text-xs mb-3">Appears in the About section of your station page</p>
        <textarea
          value={whiteLabelForm.page_description || ""}
          onChange={e => setWhiteLabelForm(prev => ({ ...prev, page_description: e.target.value }))}
          placeholder="Tell visitors about your station, format, and what makes it special..."
          className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-purple-500/50 resize-none"
          rows={4}
          data-testid="station-description"
        />
      </div>

      {/* Timezone */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
        <h4 className="text-white font-semibold mb-1">Station Timezone</h4>
        <p className="text-white/40 text-xs mb-3">Sets the clock displayed on your player and station page</p>
        <select
          value={whiteLabelForm.timezone || "America/Chicago"}
          onChange={e => setWhiteLabelForm(prev => ({ ...prev, timezone: e.target.value }))}
          className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-purple-500/50 appearance-none cursor-pointer"
          data-testid="timezone-select"
        >
          <optgroup label="US Timezones">
            <option value="America/New_York">Eastern (ET) — New York</option>
            <option value="America/Chicago">Central (CT) — Chicago</option>
            <option value="America/Denver">Mountain (MT) — Denver</option>
            <option value="America/Los_Angeles">Pacific (PT) — Los Angeles</option>
            <option value="America/Anchorage">Alaska (AKT) — Anchorage</option>
            <option value="Pacific/Honolulu">Hawaii (HT) — Honolulu</option>
          </optgroup>
          <optgroup label="Americas">
            <option value="America/Toronto">Toronto</option>
            <option value="America/Mexico_City">Mexico City</option>
            <option value="America/Bogota">Bogota</option>
            <option value="America/Sao_Paulo">Sao Paulo</option>
            <option value="America/Argentina/Buenos_Aires">Buenos Aires</option>
          </optgroup>
          <optgroup label="Europe">
            <option value="Europe/London">London (GMT)</option>
            <option value="Europe/Paris">Paris (CET)</option>
            <option value="Europe/Berlin">Berlin (CET)</option>
            <option value="Europe/Moscow">Moscow (MSK)</option>
          </optgroup>
          <optgroup label="Africa">
            <option value="Africa/Lagos">Lagos (WAT)</option>
            <option value="Africa/Johannesburg">Johannesburg (SAST)</option>
            <option value="Africa/Nairobi">Nairobi (EAT)</option>
            <option value="Africa/Cairo">Cairo (EET)</option>
          </optgroup>
          <optgroup label="Asia & Pacific">
            <option value="Asia/Dubai">Dubai (GST)</option>
            <option value="Asia/Kolkata">India (IST)</option>
            <option value="Asia/Singapore">Singapore (SGT)</option>
            <option value="Asia/Tokyo">Tokyo (JST)</option>
            <option value="Asia/Shanghai">Shanghai (CST)</option>
            <option value="Australia/Sydney">Sydney (AEST)</option>
            <option value="Pacific/Auckland">Auckland (NZST)</option>
          </optgroup>
        </select>
      </div>

      {/* Station Location (for Weather) */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
        <h4 className="text-white font-semibold mb-1">Station Location</h4>
        <p className="text-white/40 text-xs mb-3">Used for the weather widget on your player and station page</p>
        <input
          type="text"
          value={whiteLabelForm.weather_city || ""}
          onChange={e => setWhiteLabelForm(prev => ({ ...prev, weather_city: e.target.value }))}
          className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-purple-500/50"
          placeholder="e.g. Baltimore, MD"
          data-testid="weather-city-input"
        />
      </div>

      {/* Layout Presets */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
        <h4 className="text-white font-semibold mb-1">Layout Presets</h4>
        <p className="text-white/40 text-xs mb-4">Quick-start with a pre-configured layout. You can customize after applying.</p>
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {Object.entries(LAYOUT_PRESETS).map(([key, preset]) => {
            const isActive = activePreset === key;
            const PresetIcon = preset.icon;
            return (
              <button key={key} onClick={() => applyPreset(key)}
                className={`relative text-left p-4 rounded-xl border-2 transition-all ${
                  isActive ? "border-purple-500/50 bg-purple-500/10" : "border-white/5 bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/10"
                }`}
                data-testid={`preset-${key}`}
              >
                {isActive && (
                  <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-purple-500 flex items-center justify-center">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                )}
                <div className="w-8 h-8 rounded-lg mb-2 flex items-center justify-center" style={{ background: preset.preview }}>
                  <PresetIcon className="w-4 h-4 text-white/60" />
                </div>
                <p className="text-white text-sm font-semibold">{preset.label}</p>
                <p className="text-white/40 text-[11px] mt-0.5 line-clamp-2">{preset.desc}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Theme & Colors */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-6" data-testid="theme-colors-section">
        <div className="flex items-center gap-2 mb-1">
          <Palette className="w-5 h-5 text-purple-400" />
          <h4 className="text-white font-semibold">Theme & Colors</h4>
        </div>
        <p className="text-white/40 text-xs mb-5">Choose a preset theme or customize individual colors</p>

        {/* Preset Themes */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-6">
          {COLOR_THEMES.map(theme => {
            const isActive = whiteLabelForm.primary_color === theme.colors.primary_color
              && whiteLabelForm.bg_color === theme.colors.bg_color;
            return (
              <button
                key={theme.key}
                onClick={() => setWhiteLabelForm(prev => ({ ...prev, ...theme.colors }))}
                className={`relative p-3 rounded-xl border-2 transition-all text-left ${
                  isActive ? "border-white/30 bg-white/[0.06]" : "border-white/5 bg-white/[0.02] hover:border-white/10"
                }`}
                data-testid={`theme-${theme.key}`}
              >
                {isActive && (
                  <div className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-white flex items-center justify-center">
                    <Check className="w-2.5 h-2.5 text-black" />
                  </div>
                )}
                <div className="flex gap-1 mb-2">
                  {[theme.colors.primary_color, theme.colors.secondary_color, theme.colors.bg_color].map((c, i) => (
                    <div key={i} className="w-5 h-5 rounded-full border border-white/20" style={{ background: c }} />
                  ))}
                </div>
                <p className="text-white text-xs font-semibold">{theme.label}</p>
              </button>
            );
          })}
        </div>

        {/* Custom Color Pickers */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {COLOR_FIELDS.map(field => (
            <div key={field.key} className="flex items-center gap-3 bg-black/20 rounded-xl px-3 py-3 border border-white/[0.06]">
              <label className="relative cursor-pointer group" data-testid={`color-picker-${field.key}`}>
                <div
                  className="w-10 h-10 rounded-lg border-2 border-white/20 group-hover:border-white/40 transition shadow-inner"
                  style={{ background: whiteLabelForm[field.key] || "#8B5CF6" }}
                />
                <input
                  type="color"
                  value={whiteLabelForm[field.key] || "#8B5CF6"}
                  onChange={e => setWhiteLabelForm(prev => ({ ...prev, [field.key]: e.target.value }))}
                  className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
                />
              </label>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium">{field.label}</p>
                <p className="text-white/30 text-[11px]">{field.desc}</p>
              </div>
              <input
                type="text"
                value={whiteLabelForm[field.key] || ""}
                onChange={e => {
                  const v = e.target.value;
                  if (/^#?[0-9A-Fa-f]{0,6}$/.test(v)) {
                    setWhiteLabelForm(prev => ({ ...prev, [field.key]: v.startsWith('#') ? v : `#${v}` }));
                  }
                }}
                className="w-20 bg-black/30 border border-white/10 rounded-lg px-2 py-1.5 text-white/60 text-xs font-mono text-center focus:outline-none focus:border-purple-500/50"
                placeholder="#HEX"
                data-testid={`hex-input-${field.key}`}
              />
            </div>
          ))}
        </div>

        {/* Live Preview Mini */}
        <div className="mt-5 p-4 rounded-xl border border-white/[0.06] overflow-hidden" style={{ background: whiteLabelForm.bg_color || "#050505" }}>
          <p className="text-[10px] uppercase tracking-wider mb-2" style={{ color: `${whiteLabelForm.text_color || "#FFFFFF"}66` }}>Live Preview</p>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${whiteLabelForm.card_color || "#FFFFFF"}0A`, border: `1px solid ${whiteLabelForm.card_color || "#FFFFFF"}15` }}>
              <Radio className="w-5 h-5" style={{ color: whiteLabelForm.primary_color || "#8B5CF6" }} />
            </div>
            <div>
              <p className="text-sm font-bold" style={{ color: whiteLabelForm.text_color || "#FFFFFF" }}>Station Name</p>
              <p className="text-xs" style={{ color: `${whiteLabelForm.text_color || "#FFFFFF"}80` }}>Your tagline here</p>
            </div>
            <button className="ml-auto px-3 py-1.5 rounded-full text-xs font-bold text-white" style={{ background: whiteLabelForm.primary_color || "#8B5CF6" }}>
              LISTEN
            </button>
          </div>
          <div className="flex gap-2 mt-3">
            <div className="flex-1 p-2 rounded-lg text-[10px] font-medium" style={{ background: `${whiteLabelForm.card_color || "#FFFFFF"}08`, border: `1px solid ${whiteLabelForm.card_color || "#FFFFFF"}12`, color: `${whiteLabelForm.text_color || "#FFFFFF"}CC` }}>
              Sample card widget
            </div>
            <div className="flex-1 p-2 rounded-lg text-[10px] font-medium" style={{ background: `${whiteLabelForm.card_color || "#FFFFFF"}08`, border: `1px solid ${whiteLabelForm.card_color || "#FFFFFF"}12`, color: whiteLabelForm.secondary_color || "#22C55E" }}>
              Secondary accent
            </div>
          </div>
        </div>
      </div>

      {/* Section Layout — Drag & Drop */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-6" data-testid="section-layout">
        <div className="flex items-center justify-between mb-1">
          <h4 className="text-white font-semibold flex items-center gap-2">
            <GripVertical className="w-4 h-4 text-purple-400" /> Section Layout
          </h4>
          <div className="flex items-center gap-1.5 text-[10px] text-white/30">
            <span className="text-blue-400 bg-blue-400/10 px-1.5 py-0.5 rounded font-bold">MAIN</span>
            <span className="text-purple-400 bg-purple-400/10 px-1.5 py-0.5 rounded font-bold">SIDEBAR</span>
            <span className="text-orange-400 bg-orange-400/10 px-1.5 py-0.5 rounded font-bold">PLAYER</span>
          </div>
        </div>
        <p className="text-white/40 text-xs mb-4">Drag to reorder, toggle to show/hide. Player card is locked at the top.</p>

        {/* Layout Presets */}
        <div className="flex flex-wrap gap-2 mb-4">
          {ORDER_PRESETS.map(preset => {
            const PresetIcon = preset.icon;
            const isActive = JSON.stringify(fullOrder) === JSON.stringify(preset.order);
            return (
              <button key={preset.key} onClick={() => applyOrderPreset(preset)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition border ${
                  isActive ? "bg-purple-500/15 border-purple-500/30 text-purple-400" : "bg-white/[0.02] border-white/[0.06] text-white/40 hover:text-white/60 hover:bg-white/[0.04]"
                }`}
                data-testid={`preset-${preset.key}`}
              >
                <PresetIcon className="w-3.5 h-3.5" /> {preset.label}
              </button>
            );
          })}
        </div>

        {/* Locked Player Card */}
        <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-white/[0.06] bg-white/[0.02] mb-2 opacity-60">
          <Lock className="w-4 h-4 text-white/20 flex-shrink-0" />
          <div className="w-7 h-7 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0">
            <Radio className="w-3.5 h-3.5 text-purple-400" />
          </div>
          <p className="text-white/50 text-sm font-medium">Player Card</p>
          <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded text-orange-400 bg-orange-400/10">Locked</span>
        </div>

        {/* Draggable Sections */}
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={fullOrder} strategy={verticalListSortingStrategy}>
            <div className="space-y-1.5">
              {fullOrder.map(id => {
                const section = SECTION_MAP[id];
                if (!section) return null;
                return (
                  <SortableItem key={id} id={id} section={section}
                    enabled={whiteLabelForm[section.toggleKey] ?? false}
                    onToggle={toggleSection}
                    form={whiteLabelForm} setForm={setWhiteLabelForm}
                  />
                );
              })}
            </div>
          </SortableContext>
        </DndContext>
      </div>

      {/* Save Button */}
      <div className="flex items-center justify-between">
        {whiteLabelSuccess && <span className="text-green-400 text-sm">{whiteLabelSuccess}</span>}
        <button onClick={handleSaveWhiteLabel} disabled={savingWhiteLabel}
          className="ml-auto bg-purple-500 hover:bg-purple-600 disabled:opacity-50 text-white px-8 py-3 rounded-xl font-semibold transition flex items-center gap-2"
          data-testid="save-page-builder">
          {savingWhiteLabel ? "Saving..." : "Save Page Settings"}
        </button>
      </div>
    </div>
  );
};
